import React, { useState, useContext } from 'react'

import proj4 from 'proj4'
import { register } from 'ol/proj/proj4'
import { transform } from 'ol/proj'
import MapContext from '../Map/MapContext'
import Tool from '../Tool/Tool'

import { useTranslation } from 'react-i18next'

import './CenterOnCoordinates.css'

import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import { Vector as VectorSource } from 'ol/source'
import { Vector as VectorLayer } from 'ol/layer'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'

function CenterOnCoordinates() {
  const [longitude, setLongitude] = useState('')
  const [latitude, setLatitude] = useState('')
  const [coordinateSystem, setCoordinateSystem] = useState('EPSG:4326')
  const [error, setError] = useState('')

  const { map } = useContext(MapContext)
  const { t } = useTranslation()

  // Define las definiciones de proyección en un lugar central
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs')
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')
  proj4.defs('EPSG:23030', '+proj=utm +zone=30 +ellps=clrk66 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs') // EPSG:23030
  proj4.defs('EPSG:900913', '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext +no_defs') // EPSG:900913
  proj4.defs('EPSG:3857', '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext +no_defs') // EPSG:3857

  register(proj4)

  /**
   * Posiciona marcador en las coordenadas indicadas.
   * Llamada: posicionarMarcador(evt.coordinate, map);
   * @param {*} coordenadas
   * @param {*} map
   */
  function posicionarMarcador(coordenadas, map) {
    eliminarMarcador()
    const iFILEeature = new Feature({
      geometry: new Point(coordenadas)
    })
    const iconStyle = new Style({
      image: new CircleStyle({
        radius: 10,
        fill: new Fill({ color: 'rgba(255, 0, 0, 1' }),
        stroke: new Stroke({ color: 'rgba(255, 0, 0, 0.2)', width: 20 })
      })
    })
    iFILEeature.setStyle(iconStyle)

    const vectorSourceMarker = new VectorSource({
      features: [iFILEeature]
    })

    const vectorLayerMarker = new VectorLayer({
      source: vectorSourceMarker,
      name: 'marcador',
      zIndex: 1200
    })

    map.addLayer(vectorLayerMarker)
  }

  /**
   * Elimina el marcador previamente agregado al mapa.
   */
  function eliminarMarcador() {
    map.getLayers().forEach(function (layer) {
      if ((layer instanceof VectorLayer) && (layer.get('name') === 'marcador')) {
        map.removeLayer(layer)
      }
    })
  }

  /**
   * Zoom a las coordenadas
   * @param {*} coordinates
   * @param {*} zoom
   * @param {*} map
   */
  function zoomTo(coordinates, zoom) {
    map.getView().setCenter(coordinates)
    map.getView().setZoom(zoom)
  }

  const handleSearch = () => {
    if (!isNumeric(longitude) || !isNumeric(latitude)) {
      setError(t('The coordinates are not correct'))
      return
    }

    setError('')
    const epgs3857 = transformTo3857From(coordinateSystem, longitude, latitude)
    // Haz lo que necesites con epgs3857 (por ejemplo, zoomTo y posicionarMarcador)
    zoomTo(epgs3857, 13)
    posicionarMarcador(epgs3857, map)
  }

  const isNumeric = (value) => {
    return !isNaN(parseFloat(value)) && isFinite(value)
  }

  /**
   * Transforma valores long y lat a proyección EPSG:3857
   * Debe estar siempre en EPSG:3857
   * @param {*} projOrigen
   * @param {*} long
   * @param {*} lat
   * @returns
   */
  function transformTo3857From(coordinateSystem, long, lat) {
    let projOrigen

    // Define las definiciones de proyección para los sistemas de coordenadas que estás utilizando
    if (coordinateSystem === 'EPSG:4326') {
      projOrigen = 'EPSG:4326'  // WGS84 Geográficas
    } else if (coordinateSystem === 'EPSG:25830') {
      projOrigen = 'EPSG:25830'  // ETRS89 UTM huso 30N
    } else if (coordinateSystem === 'EPSG:23030') {
      projOrigen = 'EPSG:23030'  // UTM 30N ED50
    } else if (coordinateSystem === 'EPSG:900913' || coordinateSystem === 'EPSG:3857') {
      projOrigen = 'EPSG:3857'  // Spherical Mercator (900913 y 3857 son equivalentes)
    } else {
      // Manejo de errores si el sistema de coordenadas no es reconocido
      console.error('Sistema de coordenadas no reconocido:', coordinateSystem)
      return null
    }
    

    const epgs3857 = transform([Number.parseFloat(long), Number.parseFloat(lat)], projOrigen, 'EPSG:3857')
    return epgs3857
  }

  // Contenido del sidebar
  const sidebarContent = (
    <>
      <div className='form-group coordinates'>
        <label id='sist-coordenates' className='fw-bold' htmlFor='sist-coordenates'>{t('Coordinate System')}:</label>
        <select
          className='form-control mt-1'
          id='sistema-coordenadas'
          value={coordinateSystem}
          onChange={(e) => setCoordinateSystem(e.target.value)}
        >
          <option data-units='d' value='EPSG:4326'>
            EPSG:4326 - WGS84 Geográficas (4326)
          </option>
          <option data-units='m' value='EPSG:25830'>
            EPSG:25830 - ETRS89 UTM huso 30N (25830)
          </option>
          <option data-units='m' value='EPSG:23030'>
            EPSG:23030 - UTM 30N ED50 (23030)
          </option>
          <option data-units='m' value='EPSG:900913'>
            EPSG:900913 - Spherical Mercator (900913)
          </option>
          <option data-units='m' value='EPSG:3857'>
            EPSG:3857 - Spherical Mercator (3857)
          </option>
        </select>
      </div>
      <div id='error-coordinates' className='text-danger text-center font-weight-bold'>
        {error}
      </div>
      <div className='form-group coordinates mt-2 mb-2'>
        <input
          type='text'
          className='form-control'
          id='longitud'
          placeholder= {t('Longitude / X')}
          value={longitude}
          onChange={(e) => setLongitude(e.target.value)}
        />
        <input
          type='text'
          className='form-control'
          id='latitud'
          placeholder={t('Latitude / Y')}
          value={latitude}
          onChange={(e) => setLatitude(e.target.value)}
        />
      </div>
      <div id='footer'>
        <button className='btn btn-primary w-100 mt-2' id='bSearchCoordenatesButtonTextByProjection' onClick={handleSearch}>
          {t('Locate coordinates')}
        </button>
        <p />
        <button className='btn btn-secondary w-100' id='bSearchCoordenatesClean' onClick={eliminarMarcador}>
        {t('Clear marker')}
        </button>
      </div>
    </>
  )

  return (
    <Tool
      id="centerOnCoordinates"
      type="toggleable"
      icon='bi bi-person-walking'
      label={t('Go to coordinates')}
      sidebarTitle={t('Go to coordinates')}
      hasSidebar
      sidebarContent={sidebarContent}
    />
  )
}

export default CenterOnCoordinates
